<template>
  <layout-user-dashboard>
    <template #default>
      <h1 class="margin-s-bottom">{{ tt("userDashboard.reviews.pageTitle") }}</h1>
      <p class="text-l margin-max-bottom">
        {{ tu("userDashboard.reviews.introText") }}
      </p>

      <spinner v-if="isLoadingReviewableData" size="2" class="margin-xl-bottom"></spinner>

      <div
        v-if="reviewableProducts && reviewableProducts.length > 0"
        class="margin-xl-bottom">
        <card title="userDashboard.reviews.addReviewTitle" layout="tight">
          <div class="flex gap-l flex-bottom">
            <form-input
              v-model="reviewItemIndex"
              type="select2"
              label="userDashboard.reviews.selectProductToReviewLabel"
              :translate-list="false"
              :list="reviewProductOptions"></form-input>
            <form-button
              icon="plus"
              :disabled="!reviewItem"
              theme="success"
              text="userDashboard.reviews.addReviewCta"
              @click="startAddReviewByState()"></form-button>
          </div>
          <drawer
            v-model="isCreatingReview"
            width="800"
            title="userDashboard.reviews.addReviewTitle">
            <create-review
              v-if="isCreatingReview"
              :show-notifications="false"
              :product-id="reviewItem.product.id"
              :product="reviewItem.product"
              @create-review:created="handleReviewCreated"></create-review>
          </drawer>
        </card>
      </div>
      <custom-crud-list v-bind="listConfiguration" ref="reviewList">
        <template #cell-product="{ item }">
          <span v-if="!item.Product">-</span>
          <span v-if="item.Product">{{item.Product.name}}</span>
          <span v-if="item.Vendor">&nbsp;({{item.Vendor.name}})</span>
        </template>
        <template #cell-title="{ item }">
          <span
            v-tooltip="{ content: item.title, translate: false }"
            class="text-overflow-ellipsis inline-block max-width-150 cursor-pointer">
            {{ item.title }}
          </span>
        </template>
        <template #cell-text="{ item }">
          <span
            v-tooltip="{ content: item.text, translate: false }"
            class="text-overflow-ellipsis inline-block max-width-150 cursor-pointer">
            {{ item.text }}
          </span>
        </template>
        <template #cell-productRating="{ item }">
          <form-input
            v-model="item.productRating"
            type="starRating"
            :icon-size="1.75"
            disabled="true"
            :auto-input-styling="false"
            theme-style="none">
          </form-input>
        </template>
        <template #cell-vendorRating="{ item }">
          <form-input
            v-model="item.vendorRating"
            type="starRating"
            :icon-size="1.75"
            disabled="true"
            :auto-input-styling="false"
            theme-style="none">
          </form-input>
        </template>
      </custom-crud-list>
    </template>
  </layout-user-dashboard>
</template>

<script>
import { computed } from "vue";
import CustomCrudList from "@/client/components/crud/customV1/List.vue";
import asyncOperations from "@/client/extensions/composition/asyncOperations";
import CreateReview from "@/client/components/ecommerce/review/CreateReview";
export default {
  components: {
    CustomCrudList,
    CreateReview,
  },
  layout: {
    component: "default",
    params: {
      title: "userDashboard.reviews.pageTitle",
      subTitle: "userDashboard.reviews.subTitle",
      containerClass: "margin-top-top margin-bottom-top padding-remove",
    },
  },
  middleware: {
    requireUser: {},
  },
  setup(props) {
    let { asyncOps, asyncOpsReady, asyncStatus } = asyncOperations(props);
    return { asyncOps, asyncOpsReady, asyncStatus };
  },
  data: function () {
    return {
      orders: false,
      reviews: false,
      reviewItemIndex: false,
      reviewData: false,
      isCreatingReview: false,
      asyncData: {
        reviews: {
          target: "entity/review",
          data: {
            pagination: { start: 0, pageSize: 100 },
            filters: {
              OwnerId: computed(() => this.$store.getters["user/profile"]?.id),
            },
          },
          options: {
            dataMutator: (res) => {
              return res.items;
            },
          },
        },
        orders: {
          target: "ecommerce/order",
          data: {
            pagination: { start: 0, pageSize: 100 },
            filters: {
              OwnerId: computed(() => this.$store.getters["user/profile"]?.id),
            },
          },
          options: {
            dataMutator: (res) => {
              return res.items;
            },
          },
        },
      },
      listConfiguration: {
        subject: "review",
        listSrc: "entity/review",
        showIdField: true, // bool deafult true
        sortable: false, //bool, default false
        publishable: false, //bool, default false
        enableMassActions: false,
        editRoutePrefix: "admin-", //**
        createRoutePrefix: "admin-", //**
        deleteRoutePrefix: "ecommerce/", //**
        additionalListData: {
          simplified: false,
          translate: false,
        },
        constantFilters: {
          OwnerId: computed(() => this.$store.getters["user/profile"]?.id),
        },
        fields: {
          // these are the table columns
          product: {
            itemKey: "product",
            label: "userDashboard.reviews.fields.product",
          },
          title: {
            itemKey: "title",
            label: "ecommerce.reviews.fields.title",
          },
          text: {
            itemKey: "text",
            label: "ecommerce.reviews.fields.text",
          },
          productRating: {
            itemKey: "productRating",
            label: "ecommerce.reviews.fields.productRating",
          },
          vendorRating: {
            itemKey: "vendorRating",
            label: "ecommerce.reviews.fields.vendorRating",
          },
        },
        actions: false,
      },
    };
  },
  computed: {
    isLoadingReviewableData() {
      if (Array.isArray(this.orders) && Array.isArray(this.reviews)) {
        return false;
      } else {
        return true;
      }
    },
    reviewableProducts() {
      if (this.isLoadingReviewableData) {
        return false;
      }

      // find all the products
      let reviewableProducts = {};

      // for each review, check the target product,
      this.orders.forEach((order) => {
        order.Quote.items.forEach((item) => {
          let product = item.cartItem.Product;
          let vendorId = item.cartItem.Product.VendorId;

          reviewableProducts[product.id] = {
            product,
            vendorId,
            isReviewed: false,
          };
        });
      });

      // check if we reviewd it already
      this.reviews.forEach((review) => {
        if (reviewableProducts[review.Product.id]) {
          reviewableProducts[review.Product.id].isReviewed = true;
        }
      });

      let finalResult = Object.values(reviewableProducts).filter(
        (item) => !item.isReviewed
      );

      return finalResult;
    },
    reviewItem() {
      if (
        !this.reviewItemIndex &&
        this.reviewItemIndex !== 0 &&
        this.reviewItemIndex !== "0"
      ) {
        return false;
      }

      return this.reviewableProducts[this.reviewItemIndex];
    },
    reviewProductOptions() {
      let result = [];
      if (!this.reviewableProducts) {
        return result;
      }

      let i = 0;
      this.reviewableProducts.forEach((item) => {
        result.push({
          value: i,
          label: item.product.name,
        });
        i++;
      });
      return result;
    },
  },
  methods: {
    startAddReviewByState() {
      if (
        this.reviewItemIndex ||
        this.reviewItemIndex === 0 ||
        this.reviewItemIndex === "0"
      ) {
        this.isCreatingReview = true;
      }
    },
    handleReviewCreated() {
      this.isCreatingReview = false;
      this.reviewItemIndex = false;
      this.refreshAsyncData();
      this.$refs["reviewList"].reloadItems();
      this.$s.ui.notification(
        "userDashboard.reviews.reviewCreatedSuccessNotification",
        "success"
      );
    },
  },
  meta: "userDashboard.reviews.browserTitle",
};
</script>

<style scoped lang="scss"></style>
